import { createElement, useState, useCallback, useEffect, useMemo } from 'react';
import { Table, Modal, Input, Button, notification } from 'antd';
import { isDef, isFunc, judgeArr, useClosure } from '../../../../utils';
import UnitSearch from '../UnitSearch';
const { Search } = Input;
const scrollProp = { y: '30vh' };
const bodyStyle = { maxHeight: '70vh', overflow: 'scroll' };
function TableSelect({ title, columns, dataSource: dataSourceProp, rowKey = 'key', value, multiple = true, placeholder = '请输入关键词', validator, validatorTip, onSearch, onCancel: onCancelProp, onOk: onOkProp, children, modalProps, tableProps, useUnitSearchQueryList, ...restProps }) {
    const [searchValue, setSearchValue] = useState(undefined);
    const { selectedValue, setSelectedValue } = useSelectedValue(value);
    const getKey = useCallback((record) => {
        if (isFunc(rowKey)) {
            return rowKey(record);
        }
        return rowKey;
    }, []);
    const { dataSource, setDataSource } = useDataSource(dataSourceProp, selectedValue, getKey);
    const { triggerTarget, visible, setVisible } = useTriggerTarget(children, dataSourceProp, setDataSource);
    const reset = () => {
        setSearchValue(undefined);
        setVisible(false);
    };
    const onCancel = useCallback(() => {
        isFunc(onCancelProp) && onCancelProp();
        reset();
    }, [onCancelProp]);
    const onOk = useCallback(useClosure(() => {
        isFunc(onOkProp) && onOkProp([...selectedValue]);
        reset();
    }), [onOkProp]);
    const inputHandler = useCallback((e) => {
        setSearchValue(e.target.value);
    }, []);
    const searchHandler = useCallback((value) => {
        isFunc(onSearch) && onSearch(value);
    }, [onSearch]);
    const operateClickHandler = useCallback(useClosure(async (record, index, type) => {
        switch (type) {
            case 'add': {
                if (!multiple && selectedValue.length >= 1) {
                    return;
                }
                const delIndex = dataSource.findIndex((item) => item[String(getKey(item))] === record[String(getKey(record))]);
                let valid = isDef(validator) ? validator(dataSource[delIndex]) : true;
                if (typeof valid !== 'boolean' && valid instanceof Promise) {
                    valid = await valid;
                }
                if (!valid) {
                    validatorTip && notification.info({ message: validatorTip });
                    return;
                }
                const res = [...dataSource];
                res.splice(delIndex, 1);
                setSelectedValue([...selectedValue, record]);
                setDataSource(res);
                break;
            }
            case 'del': {
                const delIndex = selectedValue.findIndex((item) => item[String(getKey(item))] === record[String(getKey(record))]);
                const res = [...selectedValue];
                res.splice(delIndex, 1);
                setSelectedValue(res);
                setDataSource([...dataSource, record]);
                break;
            }
            default:
                break;
        }
    }), []);
    const getOperateColumn = useCallback((type) => ({
        title: '操作',
        dataIndex: '',
        key: '',
        width: 100,
        render: (text, record, index) => {
            const operateBtn = (createElement(Button, { type: "link", onClick: () => {
                    operateClickHandler(record, index, type);
                } }, type === 'add' ? '添加' : '删除'));
            if (type === 'add') {
                if (!multiple && selectedValue.length >= 1) {
                    return (createElement(Button, { type: "link", disabled: true }, "\u6DFB\u52A0"));
                }
                return operateBtn;
            }
            return operateBtn;
        },
    }), [selectedValue]);
    return (createElement("div", { className: "table-select-wrapper", style: { display: 'inline-block' }, ...restProps },
        triggerTarget,
        createElement(Modal, { title: title, visible: visible, width: 750, bodyStyle: bodyStyle, onCancel: onCancel, onOk: onOk, getContainer: false, ...modalProps },
            isDef(onSearch) && (createElement("div", null, useUnitSearchQueryList ? (createElement(UnitSearch, { value: searchValue, enterButton: true, reset: searchValue === undefined, queryList: useUnitSearchQueryList, placeholder: placeholder, onChange: (val) => {
                    setSearchValue(val);
                    searchHandler(val);
                } })) : (createElement(Search, { className: "search-input", enterButton: true, placeholder: placeholder, value: searchValue, onChange: inputHandler, onSearch: searchHandler })))),
            createElement(Table, { bordered: true, className: "result-table", scroll: scrollProp, ...tableProps, dataSource: dataSource, columns: [...columns, getOperateColumn('add')], rowKey: rowKey }),
            createElement(Table, { bordered: true, className: "selected-table", scroll: scrollProp, ...tableProps, columns: [...columns, getOperateColumn('del')], dataSource: selectedValue, rowKey: rowKey }))));
}
function useDataSource(dataSourceProp, selectedValue, getKey) {
    const [dataSource, setDataSource] = useState(dataSourceProp);
    useEffect(() => {
        const keys = selectedValue.map((record) => record[getKey(record)]);
        const res = dataSourceProp.filter((record) => {
            const key = record[getKey(record)];
            return !keys.includes(key);
        });
        setDataSource(res);
    }, [dataSourceProp]);
    return { dataSource, setDataSource };
}
function useSelectedValue(value) {
    const [selectedValue, setSelectedValue] = useState(value || []);
    useEffect(() => {
        if (isDef(value)) {
            setSelectedValue(value);
        }
    }, [value]);
    return { selectedValue, setSelectedValue };
}
function useTriggerTarget(children, dataSourceProp, setDataSource) {
    const [visible, setVisible] = useState(false);
    const defaultTarget = useMemo(() => createElement(Button, { type: "link" }, "\u8BF7\u9009\u62E9"), []);
    const [triggerTarget, setTriggerTarget] = useState(defaultTarget);
    useEffect(() => {
        const target = getTriggerTarget(children);
        setTriggerTarget(target);
    }, [children]);
    const showClickHandler = useCallback(() => {
        if (!judgeArr(dataSourceProp)) {
            setDataSource([]);
        }
        setVisible(true);
    }, []);
    const getTriggerTarget = (children) => (createElement("div", { className: "child-wrapper", onClick: showClickHandler }, isDef(children) ? children : defaultTarget));
    return {
        triggerTarget,
        visible,
        setVisible,
    };
}
export default TableSelect;
